/**
 * The composition root.
 *
 * Everything below this file takes its protocol helpers by injection so it can
 * be tested without crypto and built in parallel with `@setu/protocol`. This is
 * the one place that reaches for the real implementations and wires them
 * together, so an app never has to know which pieces need which helper — and
 * there is exactly one place to audit that verification is actually connected.
 *
 * This is also the only module in `core` that imports `@setu/protocol` at
 * runtime rather than for types.
 */

import type { EventTemplate, Hex32, NostrEvent } from "@setu/protocol";
import {
  isValidEventShape,
  matchesFilter,
  sameRelay,
  verifyEventSignature,
} from "@setu/protocol";
import type {
  EventStore,
  EventVerifier,
  ProfileBatcher,
  RelayPool,
  SubscriptionManager,
} from "./contracts";
import { DefaultProfileBatcher } from "./profiles/profileBatcher";
import { OutboxRouter, type OutboxRouterOptions } from "./relay/outboxRouter";
import { RelayInfoCache } from "./relay/relayInfoCache";
import { WebSocketRelayPool } from "./relay/relayPool";
import { createRelayScorecardSource } from "./relay/relayScorecardSource";
import type { CreateSocket } from "./relay/socket";
import { DefaultSubscriptionManager } from "./relay/subscriptionManager";
import { MemoryEventStore } from "./store/memoryStore";
import { BatchingEventVerifier } from "./verify/verifier";

export interface EngineOptions {
  /**
   * Relays used when a read names no author-specific route, and for publishing
   * when the caller does not name relays.
   */
  readonly relays: readonly string[];
  /**
   * Signs NIP-42 challenges. Omit for a session with no key.
   *
   * Kept out of the pool's construction args in spirit: the pool asks, the app
   * decides whether identifying to a relay is acceptable.
   */
  readonly signAuth?: (template: EventTemplate) => Promise<NostrEvent>;
  /**
   * Relays that count as *chosen* even though they are not in {@link relays}, and
   * whose NIP-42 challenges may therefore be answered.
   *
   * Consulted in addition to the configured set, never instead of it. It exists for
   * one concrete case: NIP-17 gift wraps are read from the account's own kind-10050
   * inbox list, which usually names relays outside `relays` and frequently ones that
   * set `auth_required`. An inbox relay whose challenge goes unanswered returns
   * silence, so the account is reachable by private message and sees none of them.
   *
   * The rule `relay/relayAuth.ts` states is unchanged: a signed AUTH event tells a
   * relay exactly who is reading, so only relays the account itself named are
   * eligible. A predicate rather than a second array because the answer changes
   * while the engine lives — an inbox list arrives after the first render — and
   * rebuilding the engine to widen it would drop every live subscription. Core does
   * not fetch that list, so the app supplies the predicate rather than core keeping
   * a list it cannot verify the provenance of.
   */
  readonly alsoAuthenticate?: (relay: string) => boolean;
  /**
   * Store implementation. Defaults to in-memory, and must keep doing so: this
   * package runs in Node for `apps/cli` and for every test, where IndexedDB does
   * not exist.
   *
   * An app that wants persistence builds it with `store/persistentStore`'s
   * `createPersistentStore`, which scopes the database to the account, injects the
   * same helpers this file uses (`protocolHelpers` below) and degrades to memory
   * when storage is unavailable. Passing a bare `DexieEventStore` skips that last
   * part.
   *
   * Lifetime stays with whoever built it — see {@link Engine.close}.
   */
  readonly store?: EventStore;
  /**
   * The signed-in pubkey, if any. Used only for the NIP-70 publish check: with it
   * absent, this engine refuses to rebroadcast any protected event, because it
   * cannot tell one of ours from someone else's.
   */
  readonly accountPubkey?: Hex32;
  /** Socket factory. Node callers pass a `ws` adapter; browsers can omit it. */
  readonly createSocket?: CreateSocket;
  readonly outbox?: Omit<OutboxRouterOptions, "store" | "fallbackRelays">;
  readonly onNotice?: (relay: string, message: string) => void;
  readonly onError?: (scope: string, error: unknown) => void;
}

export interface Engine {
  readonly store: EventStore;
  readonly pool: RelayPool;
  readonly verifier: EventVerifier;
  readonly subscriptions: SubscriptionManager;
  readonly outbox: OutboxRouter;
  readonly profiles: ProfileBatcher;
  /**
   * What each relay says it can do (NIP-11).
   *
   * Exposed on the engine so a settings screen can show it and a caller can route
   * by capability — "which of my relays answers COUNT" is a question only this can
   * answer, and asking one that cannot is a query that returns silence.
   */
  readonly relayInfo: RelayInfoCache;
  readonly relays: readonly string[];
  /**
   * Closes the relay pool. Deliberately *not* the store: a caller that passed one
   * in may outlive this engine, and closing a store out from under its owner would
   * drop live observers the owner still holds. Whoever built the store closes it —
   * a persistent one holds an IndexedDB connection, so on an account switch that
   * call is what stops the previous account's handle leaking for the life of the
   * tab.
   */
  close(): void;
}

/**
 * The real protocol helpers, bundled for passing into any component that takes
 * them. Exported so an app constructing its own store gets the same behavior as
 * the engine's.
 */
export const protocolHelpers = {
  matchesFilter,
  isValidEventShape,
  verifyEventSignature,
} as const;

/**
 * Whether this account chose `relay`, and may therefore prove who it is to it.
 *
 * Its own function so the rule is testable without a socket: it is the whole of
 * Setu's answer to "may this relay learn the reader's pubkey", and the two halves
 * come from different places — a configured list the user typed, and a predicate
 * over lists the app fetched. Compared with `sameRelay` rather than by string, so a
 * challenge from `wss://Relay.Example/` is still recognised as the relay the
 * account configured as `wss://relay.example`.
 */
export function isChosenRelay(
  options: Pick<EngineOptions, "relays" | "alsoAuthenticate">,
  relay: string,
): boolean {
  return (
    options.relays.some((configured) => sameRelay(configured, relay)) ||
    options.alsoAuthenticate?.(relay) === true
  );
}

/** Build a fully wired engine. */
export function createEngine(options: EngineOptions): Engine {
  const onError = options.onError ?? (() => {});

  const store =
    options.store ??
    new MemoryEventStore({
      matchesFilter,
      isValidEventShape,
      onError: (e) => onError("store", e),
    });

  // Verification is wired here and nowhere else. There is no option to disable
  // it: a client that trusts a relay for authenticity is not a Nostr client.
  const verifier = new BatchingEventVerifier({
    verifySignature: verifyEventSignature,
    isValidEventShape,
  });

  const pool = new WebSocketRelayPool({
    ...(options.createSocket ? { createSocket: options.createSocket } : {}),
    /*
     * NIP-42. Without a signer the pool never authenticates, which is correct for
     * a signed-out session: there is no identity to prove.
     *
     * Scoped to the relays this account chose: `options.relays`, plus whatever
     * `alsoAuthenticate` recognises — the app's own inbox list, in practice. A relay
     * the account chose has already been trusted with its traffic; one encountered
     * through outbox routing has not, and answering its challenge would hand it a
     * pubkey it had no other way to learn.
     */
    ...(options.signAuth ? { signAuth: options.signAuth } : {}),
    // Read on every challenge rather than resolved once: an inbox list arrives
    // after the engine is built, and an allowance frozen at construction would
    // leave the relay holding this account's private mail permanently anonymous.
    shouldAuthenticate: (relay) => isChosenRelay(options, relay),
    isValidEventShape,
    onNotice: options.onNotice,
    onError: (relay, e) => onError(`relay:${relay}`, e),
  });

  /*
   * Relay capabilities, fetched once per relay and consulted synchronously.
   *
   * Loaded in the background and deliberately not awaited: capability data makes
   * queries better, it is never required to make them. A relay that is slow to
   * serve its NIP-11 document must not delay the first feed.
   */
  const relayInfo = new RelayInfoCache({
    onError: (relay, e) => onError(`relay-info:${relay}`, e),
  });
  /*
   * One fetch per relay, feeding both consumers.
   *
   * The connection needs `max_subscriptions` and `max_filters` to pace itself; the
   * query planner and the UI need the rest. The pool used to fetch the document
   * separately, which meant two requests per relay and two caches that could
   * disagree about the same JSON.
   */
  void relayInfo.loadAll(options.relays).then(() => {
    for (const [url, info] of relayInfo.all()) {
      pool.setRelayLimits(url, info.limitation);
    }
  });

  /*
   * Fallback routing is ordered by measured delivery, not list position.
   *
   * The router's fallbacks are capped, so order decides which relays are consulted
   * at all for an author with no relay list — and "the order the user typed their
   * relays in" is not a decision. The scorecard aggregates store provenance (which
   * relay actually delivered which kinds) so a kind-0 fallback leads with the
   * relays that have delivered profiles, a kind-30023 fallback with the ones that
   * have delivered long-form. Bootstrap-safe: before the first scan completes the
   * hook returns the input unchanged, which is exactly the old behaviour.
   */
  const scorecard = createRelayScorecardSource({
    store,
    onError: (e) => onError("relayScorecard", e),
  });
  const outbox = new OutboxRouter({
    ...options.outbox,
    store,
    fallbackRelays: options.relays,
    orderFallback: (kinds) => scorecard.order(options.relays, kinds),
  });

  const subscriptions = new DefaultSubscriptionManager({
    pool,
    store,
    verifier,
    matchesFilter,
    defaultRelays: () => options.relays,
    relayInfo: (relay) => relayInfo.get(relay),
    ownPubkey: () => options.accountPubkey,
    onError: (e) => onError("subscriptions", e),
  });

  // Given the router, profile reads go to each author's own write relays and
  // only fall back to the configured set when an author has no relay list.
  const profiles = new DefaultProfileBatcher({
    store,
    subscriptions,
    relays: options.relays,
    router: outbox,
    onError: (e) => onError("profiles", e),
  });

  return {
    store,
    pool,
    verifier,
    subscriptions,
    outbox,
    profiles,
    relayInfo,
    relays: options.relays,
    close() {
      pool.close();
    },
  };
}
